/**
 * A weapon that was rolled rather than authored.
 *
 * The record is materialized at the moment of generation and stored, rather than kept as a seed to
 * re-derive from. Re-deriving would make the generator a frozen format: retuning one affix range
 * would silently rewrite a gun somebody already owns and already learned. A record costs a few
 * bytes in a save and cannot do that.
 *
 * Magnitudes are quantized on the way in, so the record is byte-identical after a round trip
 * through JSON and so an inspect screen prints "+18%" rather than "+18.3729%".
 */

import type { AffixId } from '@/sim/items/affixContract';
import type { Rarity } from '@/sim/items/itemContract';
import type { WeaponId } from '@/sim/weapons/weaponContract';

export interface RolledWeapon {
  /** The base gun. Its spec is the starting point everything here modifies. */
  readonly weaponId: WeaponId;
  readonly rarity: Rarity;
  readonly prefixId: AffixId;
  readonly suffixId: AffixId;
  /** Positive fraction. What it means is the prefix affix's business. */
  readonly prefixMagnitude: number;
  readonly suffixMagnitude: number;
  /** Derived from the three ids, stored for display, never trusted on load. */
  readonly name: string;
}

/** Decimal places a magnitude keeps. Three is a tenth of a percent, which is below noticing. */
export const ROLL_MAGNITUDE_PLACES = 3;

const MAGNITUDE_SCALE = 10 ** ROLL_MAGNITUDE_PLACES;

/** Rounds a rolled magnitude to the stored precision. The one place that rounding happens. */
export function quantizeMagnitude(value: number): number {
  return Math.round(value * MAGNITUDE_SCALE) / MAGNITUDE_SCALE;
}
